/* eslint-disable no-underscore-dangle */
import { createFeatureSelector, createSelector, select } from '@ngrx/store';

import { safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import {
  TravelerService,
  ServiceCatalogSelectionsForAllTravelers,
  ServiceCatalogSelections,
  ServiceSelections,
} from '@fcom/dapi';
import {
  FlightSeatSelections,
  SeatMapState,
  SeatMapStatus,
  SeatMapWithStatus,
  SeatSelectionForFlight,
} from '@fcom/common/interfaces/seat-map.interface';
import { entrySet, mapValues } from '@fcom/core/utils';
import { Category, SeatmapSvgAndControlData, SubCategory } from '@fcom/dapi/api/models';

import { COMMON_BOOKING_FEATURE_KEY } from '../constants';
import { CommonBookingFeatureState, CommonBookingState } from '../store.interface';
import { getSeatPrice } from '../../modules/ancillaries/utils';
import { getBundleSelectionsForCategory } from '../utils/services.utils';

const combineSeatSelectionsWithSeatBundles = (
  seatSelections: Record<string, TravelerService[]>,
  seatBundleSelections: ServiceCatalogSelections<TravelerService[]>
) => {
  // Flatten bundle seat selections to match seatSelections structure
  const flatBundleSeatSelections = mapValues(seatBundleSelections, (selections) =>
    Object.values(selections).flat().filter(Boolean)
  );

  // We want all fragmentIds of both selections to cover all possible fragments
  // Additionally we want to keep "empty" seats so they get removed from cart during bundle addition
  return Object.keys({ ...seatSelections, ...flatBundleSeatSelections }).reduce(
    (result, flightId) => ({
      ...result,
      [flightId]: [...(seatSelections?.[flightId] || []), ...(flatBundleSeatSelections?.[flightId] || [])],
    }),
    {}
  );
};

const _selectCommonBookingState = createFeatureSelector<CommonBookingFeatureState, CommonBookingState>(
  COMMON_BOOKING_FEATURE_KEY
);

const _serviceSelections = createSelector(
  _selectCommonBookingState,
  (state: CommonBookingState) => state?.services?.selections
);

const _seatMapState = createSelector(_selectCommonBookingState, (state: CommonBookingState) => state?.seatMap);

const _seatMapStateForFlight = (flightId: string) =>
  createSelector(_seatMapState, (sm: SeatMapState) => sm?.[flightId]);

const _seatSelectionsForFlight = (flightId: string) =>
  createSelector(_seatMapStateForFlight(flightId), (sm: SeatMapWithStatus) => sm?.selections);

const _bundlesAvailable = () =>
  createSelector(_seatMapState, (sm: SeatMapState) =>
    Object.values(sm ?? {}).some((seatmap) =>
      Object.values(seatmap?.seatMap?.bundles ?? {}).some((bundles) => !!bundles?.length)
    )
  );

/**
 * Selects the seatMap from the state via given flightId.
 */
export const seatMapForFlight = (
  flightId: string
): StateSelector<CommonBookingFeatureState, SeatmapSvgAndControlData> =>
  safeSelect(createSelector(_seatMapStateForFlight(flightId), (sm: SeatMapWithStatus) => sm?.seatMap));

export const seatMapWithStatus = (flightId: string): StateSelector<CommonBookingFeatureState, SeatMapWithStatus> =>
  select(createSelector(_seatMapStateForFlight(flightId), (sm: SeatMapWithStatus) => sm));

export const seatSelectionsForFlight = (
  flightId: string
): StateSelector<CommonBookingFeatureState, FlightSeatSelections> => select(_seatSelectionsForFlight(flightId));

export const bundlesAvailable = (): StateSelector<CommonBookingFeatureState, boolean> => select(_bundlesAvailable());

export const seatSelectionsForFlightAndTraveler = (
  flightId: string,
  travelerId: string
): StateSelector<CommonBookingFeatureState, SeatSelectionForFlight> =>
  select(createSelector(_seatSelectionsForFlight(flightId), (ss: FlightSeatSelections) => ss?.[travelerId]));

export const seatMapForFlightStatus = (flightId: string): StateSelector<CommonBookingFeatureState, SeatMapStatus> =>
  select(createSelector(_seatMapStateForFlight(flightId), (ss: SeatMapWithStatus) => ss?.status));

export const seatSelections = (
  includeBundles = false
): StateSelector<CommonBookingFeatureState, ServiceCatalogSelectionsForAllTravelers> =>
  select(
    createSelector(_seatMapState, _serviceSelections, (sm: SeatMapState, serviceSelections: ServiceSelections) => {
      const selections = sm
        ? mapValues(sm, (s: SeatMapWithStatus, _cartFlightId) =>
            entrySet<SeatSelectionForFlight>(s.selections)
              .reduce<TravelerService[]>((all: TravelerService[], { key: travelerId, value: { seatNumber } }) => {
                const seatPrice = getSeatPrice(
                  s.seatMap?.seats.find((seat) => seat.seatNumber === seatNumber),
                  travelerId
                );
                return all.concat({
                  category: SubCategory.SEAT,
                  quantity: seatNumber ? 1 : 0,
                  variant: seatNumber,
                  seatNumber,
                  displayName: '',
                  title: '',
                  originalPricePerItem: seatPrice?.originalPrice ?? s.selections[travelerId].originalPricePerItem,
                  pricePerItem: seatPrice?.price ?? s.selections[travelerId].pricePerItem,
                  paid: s.selections[travelerId].paid,
                  ticketed: s.selections[travelerId].ticketed,
                  modifiable: s.selections[travelerId].modifiable,
                  travelerId: travelerId,
                  includedInTicketType: false,
                  includedInTierBenefit: false,
                });
              }, [])
              .filter(Boolean)
          )
        : undefined;

      if (includeBundles) {
        const seatBundleSelections = getBundleSelectionsForCategory(
          serviceSelections?.[Category.BUNDLE],
          Category.SEAT
        );
        return combineSeatSelectionsWithSeatBundles(selections, seatBundleSelections);
      }

      return selections;
    })
  );
